import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Paper, Typography, TextField, Button, Chip, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TablePagination, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip, Divider, CircularProgress,
  Snackbar, Alert, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  Search as SearchIcon, FileDownload as DownloadIcon, Print as PrintIcon, Visibility as ViewIcon, People as PeopleIcon, Female as FemaleIcon,
  Male as MaleIcon, Hotel as HostelIcon, WarningAmber as WarnIcon, Payments as DuesIcon, Groups as GroupsIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { classReportAPI } from '../services/api';
import { money, errorText } from '../utils/format';
import { fmtDate } from '../utils/dates';
import { printPage, esc } from '../utils/print';

function Kpi({ icon, label, value, sub, color }) {
  return (
    <Paper sx={{ p: 2, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 2, height: '100%' }}>
      <Box sx={{ width: 46, height: 46, borderRadius: 2, bgcolor: `${color}18`, color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</Box>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>{value}</Typography>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        {sub && <Typography variant="caption" sx={{ display: 'block', color }}>{sub}</Typography>}
      </Box>
    </Paper>
  );
}

const attColor = (v, low) => (v === null || v === undefined ? '#90a4ae' : low ? '#c62828' : v >= 90 ? '#2e7d32' : '#ef6c00');

export default function ClassStudents() {
  const [summary, setSummary] = useState(null);
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [detail, setDetail] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const notify = useCallback((message, severity = 'success') => setSnack({ open: true, message, severity }), []);

  useEffect(() => {
    classReportAPI.getSummary().then((r) => {
      setSummary(r.data);
      const first = r.data.classes.find((c) => c.students > 0) || r.data.classes[0];
      if (first) setClassId(first.classId);
    }).catch((e) => notify(errorText(e, 'Could not load the class summary'), 'error'));
  }, [notify]);

  useEffect(() => { const t = setTimeout(() => setDebounced(q), 350); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setSectionId(''); setPage(0); }, [classId]);

  useEffect(() => {
    if (!classId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await classReportAPI.getStudents({ classId, sectionId: sectionId || undefined, q: debounced || undefined });
        if (!cancelled) { setList(r.data); setPage(0); }
      } catch (e) { if (!cancelled) notify(errorText(e, 'Could not load students'), 'error'); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [classId, sectionId, debounced, notify]);

  const showFees = !!summary?.showFees;
  const cls = summary?.classes.find((c) => c.classId === classId);
  const students = list?.students || [];
  const shown = students.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const scope = cls ? `${cls.className}${sectionId ? ' - ' + (cls.sections.find((s) => s.sectionId === sectionId)?.sectionName || '') : ''}` : '';

  const rowsForExcel = (arr) => arr.map((s) => ({
    'Roll no': s.rollNo || '', 'Admission no': s.admissionNo, Name: s.name, Gender: s.gender || '', 'Date of birth': s.dateOfBirth || '', Age: s.age ?? '',
    'Blood group': s.bloodGroup || '', Program: s.className, Batch: s.sectionName || '', Phone: s.phone || '', Email: s.email || '', 'Guardian': s.guardianName || '',
    'Guardian phone': s.guardianPhone || '', Address: s.address || '', Hostel: s.hostel || 'Day scholar', 'Attendance %': s.attendancePct ?? '',
    'Clinical hours': s.clinicalHours, 'Clinical %': s.clinicalPct ?? '', 'Log book approved': s.logbookApproved,
    ...(showFees ? { 'Fee total (₹)': Number(s.feeTotal), 'Fee paid (₹)': Number(s.feePaid), 'Fee due (₹)': Number(s.feeDue) } : {}),
  }));

  const exportClass = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rowsForExcel(students)), 'Students');
    XLSX.writeFile(wb, `students_${scope}.xlsx`.replace(/[\\/:*?"<>|\s]+/g, '_'));
  };

  const exportAll = async () => {
    try {
      const all = (await classReportAPI.getStudents({})).data.students;
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summary.classes.map((c) => ({
        Program: c.className, Coordinator: c.coordinator || '', Students: c.students, Boys: c.boys, Girls: c.girls, 'Hostel residents': c.hostel,
        'Average attendance %': c.avgAttendance ?? '', 'Below 75%': c.belowAttendance, ...(showFees ? { 'Students with dues': c.feeDueCount, 'Fee due (₹)': Number(c.feeDue) } : {}),
      }))), 'Summary');
      const groups = new Map();
      all.forEach((s) => { const k = s.className || 'No program'; groups.set(k, [...(groups.get(k) || []), s]); });
      groups.forEach((arr, name) => XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rowsForExcel(arr)), name.replace(/[\\/?*[\]:]/g, '-').slice(0, 31)));
      XLSX.writeFile(wb, 'class_wise_students.xlsx');
      notify(`Exported ${all.length} students across ${groups.size} programs`);
    } catch (e) { notify(errorText(e, 'Could not export'), 'error'); }
  };

  const printList = async () => {
    const body = `<div class="grid">
        <div class="card"><div class="v">${students.length}</div><div class="l">Students</div></div>
        <div class="card"><div class="v">${students.filter((s) => (s.gender || '').toUpperCase() === 'MALE').length}</div><div class="l">Boys</div></div>
        <div class="card"><div class="v">${students.filter((s) => (s.gender || '').toUpperCase() === 'FEMALE').length}</div><div class="l">Girls</div></div>
        <div class="card"><div class="v red">${students.filter((s) => s.attendanceLow).length}</div><div class="l">Below ${summary.attendanceMinimum}% attendance</div></div></div>
      <table><thead><tr><th>#</th><th>Roll no</th><th>Adm. no</th><th>Name</th><th>Gender</th><th>Batch</th><th>Guardian</th><th>Phone</th><th>Hostel</th><th class="r">Att. %</th>${showFees ? '<th class="r">Fee due</th>' : ''}</tr></thead><tbody>
      ${students.map((s, i) => `<tr><td>${i + 1}</td><td>${esc(s.rollNo)}</td><td>${esc(s.admissionNo)}</td><td>${esc(s.name)}</td><td>${esc(s.gender)}</td><td>${esc(s.sectionName)}</td><td>${esc(s.guardianName)}</td><td>${esc(s.guardianPhone || s.phone)}</td><td>${esc(s.hostel || 'Day scholar')}</td><td class="r ${s.attendanceLow ? 'red b' : ''}">${s.attendancePct ?? '-'}</td>${showFees ? `<td class="r">${money(s.feeDue)}</td>` : ''}</tr>`).join('')}
      </tbody></table><div class="sig"><div>Class coordinator</div><div>Principal</div></div>`;
    printPage('Class List', scope, body);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4">Class-wise Students</Typography>
          <Typography variant="body2" color="text.secondary">Strength, attendance, hostel and fee position of every program-year, with the full student list</Typography>
        </Box>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportAll} disabled={!summary}>Export all classes (Excel)</Button>
      </Box>

      {!summary ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box> : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={2.4}><Kpi icon={<PeopleIcon />} label="Students" value={summary.totals.students} color="#00796b" sub={`${summary.classes.filter((c) => c.students > 0).length} active programs`} /></Grid>
            <Grid item xs={6} md={2.4}><Kpi icon={<FemaleIcon />} label="Girls" value={summary.totals.girls} color="#c2185b" /></Grid>
            <Grid item xs={6} md={2.4}><Kpi icon={<MaleIcon />} label="Boys" value={summary.totals.boys} color="#0277bd" /></Grid>
            <Grid item xs={6} md={2.4}><Kpi icon={<HostelIcon />} label="Hostel residents" value={summary.totals.hostel} color="#6d4c41" /></Grid>
            <Grid item xs={12} md={2.4}>
              {showFees
                ? <Kpi icon={<DuesIcon />} label="Fee dues" value={money(summary.totals.feeDue)} color="#c62828" sub={`${summary.totals.feeDueCount} students`} />
                : <Kpi icon={<WarnIcon />} label={`Below ${summary.attendanceMinimum}% attendance`} value={summary.totals.belowAttendance} color="#c62828" />}
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {summary.classes.filter((c) => c.students > 0).map((c) => {
              const active = c.classId === classId;
              const fill = c.capacity ? Math.min(100, (c.students / c.capacity) * 100) : 0;
              return (
                <Grid item xs={12} sm={6} lg={4} key={c.classId}>
                  <Paper onClick={() => setClassId(c.classId)} sx={{ p: 2.5, borderRadius: 3, cursor: 'pointer', border: '2px solid', borderColor: active ? '#00796b' : 'transparent', bgcolor: active ? '#f1faf9' : '#fff', height: '100%',
                    transition: 'all .15s', '&:hover': { boxShadow: '0 6px 20px rgba(0,0,0,0.12)' } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box><Typography fontWeight={800}>{c.className}</Typography>
                        <Typography variant="caption" color="text.secondary">{c.coordinator ? `Coordinator: ${c.coordinator}` : 'No coordinator set'}</Typography></Box>
                      <Chip size="small" icon={<GroupsIcon />} label={c.students} color="primary" />
                    </Box>
                    <LinearProgress variant="determinate" value={fill} sx={{ height: 6, borderRadius: 3, mb: 0.5, bgcolor: '#e0f2f1' }} />
                    <Typography variant="caption" color="text.secondary">{c.students} of {c.capacity ?? '-'} seats filled</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
                      <Chip size="small" label={`${c.girls} girls`} sx={{ bgcolor: '#fce4ec', color: '#ad1457' }} />
                      <Chip size="small" label={`${c.boys} boys`} sx={{ bgcolor: '#e1f5fe', color: '#01579b' }} />
                      <Chip size="small" label={`${c.hostel} in hostel`} sx={{ bgcolor: '#efebe9', color: '#4e342e' }} />
                    </Box>
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Box><Typography variant="caption" color="text.secondary">Avg attendance</Typography>
                        <Typography fontWeight={800} sx={{ color: attColor(c.avgAttendance, c.avgAttendance !== null && c.avgAttendance < summary.attendanceMinimum) }}>{c.avgAttendance ?? '-'}{c.avgAttendance !== null ? '%' : ''}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary">Below {summary.attendanceMinimum}%</Typography>
                        <Typography fontWeight={800} color={c.belowAttendance ? 'error' : 'inherit'}>{c.belowAttendance}</Typography></Box>
                      {showFees && <Box sx={{ textAlign: 'right' }}><Typography variant="caption" color="text.secondary">Fee due</Typography>
                        <Typography fontWeight={800} color={Number(c.feeDue) ? 'error' : 'inherit'}>{money(c.feeDue)}</Typography></Box>}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.5 }}>
                      {c.sections.map((s) => <Chip key={s.sectionId} size="small" variant="outlined" label={`${s.sectionName}: ${s.students}`} />)}
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
          {summary.classes.some((c) => c.students === 0) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
              Programs with no students yet: {summary.classes.filter((c) => c.students === 0).map((c) => c.className).join(', ')}.
            </Typography>
          )}

          {cls && (
            <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, borderBottom: '1px solid #eee' }}>
                <Box>
                  <Typography variant="h6" fontWeight={800}>{cls.className}</Typography>
                  <Typography variant="body2" color="text.secondary">{students.length} student{students.length === 1 ? '' : 's'} shown</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  <ToggleButtonGroup size="small" exclusive value={sectionId} onChange={(e, v) => setSectionId(v ?? '')}>
                    <ToggleButton value="">All batches</ToggleButton>
                    {cls.sections.map((s) => <ToggleButton key={s.sectionId} value={s.sectionId}>{s.sectionName} ({s.students})</ToggleButton>)}
                  </ToggleButtonGroup>
                  <TextField size="small" placeholder="Search name, roll or admission no" value={q} onChange={(e) => setQ(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} sx={{ minWidth: 260 }} />
                  <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportClass} disabled={!students.length}>Excel</Button>
                  <Button variant="outlined" size="small" startIcon={<PrintIcon />} onClick={printList} disabled={!students.length}>Print</Button>
                </Box>
              </Box>
              {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box> : (
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow>
                      <TableCell>Roll no</TableCell><TableCell>Admission no</TableCell><TableCell>Student</TableCell><TableCell>Batch</TableCell><TableCell>Guardian</TableCell>
                      <TableCell>Hostel</TableCell><TableCell align="center">Attendance</TableCell><TableCell align="center">Clinical</TableCell><TableCell align="center">Log book</TableCell>
                      {showFees && <TableCell align="right">Fee due</TableCell>}<TableCell align="center">View</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {shown.map((s) => (
                        <TableRow key={s.studentId} hover>
                          <TableCell>{s.rollNo || '-'}</TableCell>
                          <TableCell>{s.admissionNo}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700}>{s.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{s.gender || ''}{s.age !== null && s.age !== undefined ? ` · ${s.age} yrs` : ''}{s.bloodGroup ? ` · ${s.bloodGroup}` : ''}</Typography>
                          </TableCell>
                          <TableCell>{s.sectionName || '-'}</TableCell>
                          <TableCell><Typography variant="body2">{s.guardianName || '-'}</Typography><Typography variant="caption" color="text.secondary">{s.guardianPhone || s.phone || ''}</Typography></TableCell>
                          <TableCell>{s.hostel ? <Chip size="small" label={s.hostel} sx={{ bgcolor: '#efebe9' }} /> : <Typography variant="caption" color="text.secondary">Day scholar</Typography>}</TableCell>
                          <TableCell align="center"><Chip size="small" label={s.attendancePct === null ? '-' : `${s.attendancePct}%`} sx={{ fontWeight: 700, color: '#fff', bgcolor: attColor(s.attendancePct, s.attendanceLow) }} /></TableCell>
                          <TableCell align="center">{s.clinicalPct === null ? '-' : `${s.clinicalPct}%`}<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{s.clinicalHours} h</Typography></TableCell>
                          <TableCell align="center">{s.logbookApproved}</TableCell>
                          {showFees && <TableCell align="right" sx={{ fontWeight: 700, color: Number(s.feeDue) ? '#c62828' : '#2e7d32' }}>{Number(s.feeDue) ? money(s.feeDue) : 'Paid'}</TableCell>}
                          <TableCell align="center"><Tooltip title="Full details"><IconButton size="small" onClick={() => setDetail(s)}><ViewIcon fontSize="small" /></IconButton></Tooltip></TableCell>
                        </TableRow>
                      ))}
                      {shown.length === 0 && <TableRow><TableCell colSpan={showFees ? 11 : 10} align="center" sx={{ py: 5, color: 'text.secondary' }}>No students found</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                  <TablePagination component="div" count={students.length} page={page} onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage} rowsPerPageOptions={[15, 30, 60]}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
                </TableContainer>
              )}
            </Paper>
          )}
        </>
      )}

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#004d40', color: 'white' }}>{detail?.name}<Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>{detail?.className} · {detail?.sectionName} · {detail?.admissionNo}</Typography></DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {detail && (
            <Grid container spacing={1.5} sx={{ mt: 0 }}>
              {[
                ['Roll no', detail.rollNo], ['Gender', detail.gender], ['Date of birth', detail.dateOfBirth ? `${fmtDate(detail.dateOfBirth)} (${detail.age} yrs)` : null], ['Blood group', detail.bloodGroup],
                ['Phone', detail.phone], ['E-mail', detail.email], ['Guardian', detail.guardianName], ['Guardian phone', detail.guardianPhone], ['Category', detail.category],
                ['Admission date', detail.admissionDate ? fmtDate(detail.admissionDate) : null], ['Hostel', detail.hostel || 'Day scholar'],
                ['Attendance', detail.attendancePct === null ? null : `${detail.attendancePct}% (${detail.daysPresent} of ${detail.daysMarked} days)`],
                ['Clinical hours', `${detail.clinicalHours} h${detail.clinicalPct !== null ? ` (${detail.clinicalPct}% of required)` : ''}`], ['Log book entries approved', detail.logbookApproved],
                ...(showFees ? [['Fee total', money(detail.feeTotal)], ['Fee paid', money(detail.feePaid)], ['Fee due', money(detail.feeDue)]] : []),
              ].map(([k, v]) => (
                <Grid item xs={6} key={k}><Typography variant="caption" color="text.secondary">{k}</Typography><Typography variant="body2" fontWeight={600}>{v ?? '-'}</Typography></Grid>
              ))}
              <Grid item xs={12}><Typography variant="caption" color="text.secondary">Address</Typography><Typography variant="body2" fontWeight={600}>{detail.address || '-'}</Typography></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDetail(null)}>Close</Button></DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
